export interface CartData {
    id: number;
    items: CartLineItem[];
    totals: CartTotals;
    discounts: CartDiscount[];
}

export interface CartDiscount {
    type: string;
    code: string;
    included: boolean;
    discount: number;
    description: string;
}

export interface CartTotals {
    itemSubtotal: number;
    totalDiscount: number;
    itemTotal: number;
    total: number;
    totalTax: number;
    totalTaxIncluded: number;
    outstandingBalance: number;
}

export interface CartLineItem {
    id: number;
    sku: string;
    price: number;
    quantity: number;
    subtotal: number;
    discount: number;
    total: number;
    tax: CartTaxAdjustment[];
    product: CartProduct;
}

export type CartProduct = CartEventProduct | CartVoucherProduct | CartRegularProduct;

interface BaseProduct {
    id: number;
    title: string;
    type: 'event' | 'product' | 'voucher';
    thumbnail: string | null;
}

export interface CartEventProduct extends BaseProduct {
    type: 'event';
    availableCapacity: number;
    startDate: string | null;
    endDate: string | null;
    eventType: {
        id: number;
        title: string;
        aantalPersonenRegel: string | null;
        uspstekst: Array<{ usp: string }>;
    };
}

export interface CartVoucherProduct extends BaseProduct {
    type: 'voucher';
    options: {
        from: string | null;
        to: string | null;
        amount: number | null;
    };
}

export interface CartRegularProduct extends BaseProduct {
    type: 'product';
}

export interface CartTaxAdjustment {
    amount: number;
    included: boolean;
    description: string;
}

export interface CartResponse {
    data: {
        cart?: CartData;
        updateCartItem?: CartData;
        updateCartCustomer?: CartData;
        addVoucherCode?: CartData;
        removeVoucherCode?: CartData;
    };
    errors?: {
        message: string;
    }[];
}

export interface VoucherOptions {
    purchasableId: number;
    qty: number;
    options: {
        from: string;
        to: string;
        amount?: number;
    };
}

export interface CartUpdatePayload {
    email: string;
    fullName: string;
    fields: {
        naamContactpersoon: string;
        telefoon: string;
        mailAdres: string;
        contactstreet: string;
        contactpostcode: string;
        contactcity: string;
    };
    billingAddress: {
        fullName: string;
        addressLine1: string;
        postalCode: string;
        countryCode: string;
        locality: string;
        country: string;
        administrativeArea: string;
    };
    shippingAddress: {
        fullName: string;
        addressLine1: string;
        postalCode: string;
        countryCode: string;
        locality: string;
        country: string;
        administrativeArea: string;
    };
    makePrimaryShippingAddress: boolean;
}

export interface PersonalDetails {
    email: string;
    fullName: string;
    phone: string;
    address: string;
    postalCode: string;
    city: string;
    administrativeArea: string;
    countryCode: string;
    makePrimaryShippingAddress: boolean;
}

export const CartErrorKeys = {
    FETCH: 'fetchCart',
    UPDATE: 'updateCart',
    VOUCHER: 'voucher',
    QUANTITY: 'quantity',
} as const;

export type CartErrorKey = (typeof CartErrorKeys)[keyof typeof CartErrorKeys] | string;

export type CartAction =
    | { type: 'SET_CART'; payload: CartData }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_UPDATING_ITEM'; payload: number }
    | { type: 'REMOVE_UPDATING_ITEM'; payload: number }
    | { type: 'SET_ERROR'; payload: CartError }
    | { type: 'CLEAR_ERRORS' }
    | { type: 'SET_MESSAGE'; payload: string | null };

export interface CartError {
    key: CartErrorKey;
    message: string;
}
